/**
 * The earthworks job form (EW-1): full page for New job, an inline panel on the
 * job's own page for Edit. Never a modal — a job is a record.
 *
 * A blank is sent as null, never 0. On edit only the keys that changed are sent
 * (PATCH keeps the rest). Status is not edited here: a new job is Draft or
 * Scheduled, and afterwards it moves by the status actions.
 *
 * The holdback is labelled from meta.holdbackLabel (Holdback in CA, Retainage
 * in US). On a new job it may be left to the customer's default, which the API
 * resolves; turning it on with no % takes the tenant's default %.
 *
 * The site list is the chosen customer's. A change of customer clears a site of
 * another customer, so the API's site_customer_mismatch is the exception.
 */
import React, { useMemo, useState } from 'react';
import { useEwSites } from '../../hooks/useEarthworks';
import { blankToNull, holdbackLabel, toInput } from '../../lib/earthworks';
import type { EwCatalog, EwJob, EwJobWrite, EwMeta } from '../../types/earthworks';
import { EW_DATE_INPUT, EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwField, EwFormError } from './EwBits';

interface FormState {
  customer_id: string;
  site_id: string;
  job_type: string;
  contract_type: string;
  ticket_mode: string;
  contract_value: string;
  /** '' = the customer's default (create only), 'on', 'off'. */
  holdback: string;
  holdback_pct: string;
  supervisor_id: string;
  scheduled_start: string;
  scheduled_end: string;
  scope: string;
  po_number: string;
  afe_number: string;
  cost_code: string;
  notes: string;
  status: string;
}

const fromJob = (j: EwJob | null, prefill?: { customerId?: string; siteId?: string }): FormState => ({
  customer_id: j?.customerId ?? prefill?.customerId ?? '',
  site_id: j?.siteId ?? prefill?.siteId ?? '',
  job_type: j?.jobType ?? '',
  contract_type: j?.contractType ?? '',
  ticket_mode: j?.ticketMode ?? '',
  contract_value: toInput(j?.contractValue),
  holdback: j ? (j.holdbackEnabled ? 'on' : 'off') : '',
  holdback_pct: toInput(j?.holdbackPct),
  supervisor_id: j?.supervisorId ?? '',
  scheduled_start: j?.scheduledStart ?? '',
  scheduled_end: j?.scheduledEnd ?? '',
  scope: j?.scope ?? '',
  po_number: j?.poNumber ?? '',
  afe_number: j?.afeNumber ?? '',
  cost_code: j?.costCode ?? '',
  notes: j?.notes ?? '',
  status: 'draft',
});

const TEXT_KEYS: (keyof FormState)[] = [
  'contract_value', 'supervisor_id', 'scheduled_start', 'scheduled_end', 'scope', 'po_number', 'afe_number', 'cost_code', 'notes',
];

/** The write body: every set field on create, the changed ones on edit. */
export function jobWriteBody(form: FormState, original: FormState | null): EwJobWrite {
  const out: Record<string, unknown> = {};
  const changed = (k: keyof FormState) => !original || form[k] !== original[k];
  if (changed('customer_id')) out.customer_id = form.customer_id;
  if (changed('site_id')) out.site_id = form.site_id;
  for (const k of ['job_type', 'contract_type', 'ticket_mode'] as const) if (changed(k) && form[k]) out[k] = form[k];
  if (changed('holdback') && form.holdback) out.holdback_enabled = form.holdback === 'on';
  if (form.holdback !== 'off' && changed('holdback_pct')) {
    const v = blankToNull(form.holdback_pct);
    if (original || v !== null) out.holdback_pct = v;
  }
  for (const k of TEXT_KEYS) if (changed(k)) {
    const v = blankToNull(form[k]);
    if (original || v !== null) out[k] = v;
  }
  if (!original) out.status = form.status;
  return out as EwJobWrite;
}

export function EwJobForm({
  job,
  prefill,
  catalog,
  meta,
  customers,
  users,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  job: EwJob | null;
  prefill?: { customerId?: string; siteId?: string };
  catalog: EwCatalog | null;
  meta: EwMeta | null;
  customers: { id: string; name: string }[];
  users: { id: string; name: string }[];
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: EwJobWrite) => void;
}) {
  const original = useMemo(() => (job ? fromJob(job) : null), [job]);
  const [form, setForm] = useState<FormState>(() => fromJob(job, prefill));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const sites = useEwSites(form.customer_id ? { customerId: form.customer_id } : undefined);
  const hb = holdbackLabel(meta);
  const currency = meta?.currency ?? null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!form.customer_id) return setLocalError('Choose the customer.');
    if (!form.site_id) return setLocalError('Choose the site.');
    if (!form.job_type) return setLocalError('Choose the job type.');
    if (form.scheduled_start && form.scheduled_end && form.scheduled_end < form.scheduled_start)
      return setLocalError('The scheduled end cannot be before the start.');
    const body = jobWriteBody(form, original);
    if (original && Object.keys(body).length === 0) return onCancel();
    onSubmit(body);
  };

  const select = (k: keyof FormState, label: string, list: { value: string; label: string }[], blank: string) => (
    <EwField label={label}>
      <select className={EW_INPUT} aria-label={label} value={form[k]} onChange={(e) => set(k, e.target.value)}>
        <option value="">{blank}</option>
        {list.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </EwField>
  );
  const text = (k: keyof FormState, label: string) => (
    <EwField label={label}>
      <input className={EW_INPUT} aria-label={label} value={form[k]} onChange={(e) => set(k, e.target.value)} />
    </EwField>
  );

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="ew-job-form" noValidate>
      <EwFormError message={localError ?? error} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <EwField label="Customer">
          <select
            className={EW_INPUT}
            aria-label="Customer"
            value={form.customer_id}
            onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value, site_id: '' }))}
          >
            <option value="">Choose…</option>
            {job && !customers.some((c) => c.id === job.customerId) && <option value={job.customerId}>{job.customerName ?? '—'}</option>}
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </EwField>
        <EwField label="Site">
          <select className={EW_INPUT} aria-label="Site" value={form.site_id} onChange={(e) => set('site_id', e.target.value)} disabled={!form.customer_id}>
            <option value="">{form.customer_id ? 'Choose…' : 'Choose a customer first'}</option>
            {job && job.customerId === form.customer_id && !(sites.data?.data ?? []).some((s) => s.id === job.siteId) && (
              <option value={job.siteId}>{job.siteName ?? '—'}</option>
            )}
            {(sites.data?.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </EwField>
        {select('job_type', 'Job type', catalog?.jobTypes ?? [], 'Choose…')}
        {select('contract_type', 'Contract type', catalog?.contractTypes ?? [], job ? 'Choose…' : 'Time and materials (default)')}
        {select('ticket_mode', 'Field tickets', catalog?.ticketModes ?? [], job ? 'Choose…' : 'Customer default')}
        <EwField label={`Contract value${currency ? ` (${currency})` : ''}`}>
          <input className={EW_INPUT} inputMode="decimal" aria-label="Contract value" value={form.contract_value} onChange={(e) => set('contract_value', e.target.value)} />
        </EwField>
        <EwField label={hb}>
          <select className={EW_INPUT} aria-label={hb} value={form.holdback} onChange={(e) => set('holdback', e.target.value)}>
            {!job && <option value="">Customer default</option>}
            <option value="on">On</option>
            <option value="off">Off</option>
          </select>
        </EwField>
        {form.holdback !== 'off' && (
          <EwField label={`${hb} %`} hint="Blank takes the company default">
            <input className={EW_INPUT} inputMode="decimal" aria-label={`${hb} %`} value={form.holdback_pct} onChange={(e) => set('holdback_pct', e.target.value)} />
          </EwField>
        )}
        <EwField label="Supervisor">
          <select className={EW_INPUT} aria-label="Supervisor" value={form.supervisor_id} onChange={(e) => set('supervisor_id', e.target.value)}>
            <option value="">None</option>
            {job?.supervisorId && !users.some((u) => u.id === job.supervisorId) && <option value={job.supervisorId}>{job.supervisorName ?? '—'}</option>}
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </EwField>
        <EwField label="Scheduled start">
          <input type="date" className={EW_DATE_INPUT} aria-label="Scheduled start" value={form.scheduled_start} onChange={(e) => set('scheduled_start', e.target.value)} />
        </EwField>
        <EwField label="Scheduled end">
          <input type="date" className={EW_DATE_INPUT} aria-label="Scheduled end" value={form.scheduled_end} onChange={(e) => set('scheduled_end', e.target.value)} />
        </EwField>
        {text('po_number', 'PO number')}
        {text('afe_number', 'AFE number')}
        {text('cost_code', 'Cost code')}
        {!job && (
          <EwField label="Start as">
            <select className={EW_INPUT} aria-label="Start as" value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </EwField>
        )}
      </div>
      <EwField label="Scope">
        <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Scope" value={form.scope} onChange={(e) => set('scope', e.target.value)} />
      </EwField>
      <EwField label="Notes">
        <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </EwField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={EW_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={EW_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
